import { useCallback, useEffect, useMemo, useState } from 'react'
import type { WorkspaceRole } from '../../types/workspace'
import {
  resolveTabBar,
  type LockedTabItem,
  type TabBarPolicy,
  type WorkspaceTabDefaults,
} from './tabBarEngine'
import {
  saveTabPrefs,
  subscribeTabPrefs,
  subscribeWorkspaceTabDefaults,
  type TabPrefs,
} from './tabPrefsService'
import type { WorkspaceModuleSource } from '../../utils/workspaceModules'
import type { MemberTabKey, TabKey } from './tabCatalog'
import { subscribeToMyEffectiveControls } from '../memberControlsService'
import type { MemberControls } from '../../types/memberControls'

export interface UseMemberTabBarOptions {
  uid?: string
  workspaceId?: string
  role?: WorkspaceRole
  workspace?: WorkspaceModuleSource
  policy?: TabBarPolicy
  useCase?: string
  unreadByTab?: Partial<Record<MemberTabKey, boolean | number>>
  personal?: MemberTabKey[]
  effectiveControls?: MemberControls | null
  personHiddenPages?: TabKey[]
}

export interface UseMemberTabBarResult {
  pinned: MemberTabKey[]
  more: MemberTabKey[]
  locked: LockedTabItem[]
  moreHasUnread: boolean
  isTabUnread: (key: MemberTabKey) => boolean
  savePersonalTabs: (tabs: MemberTabKey[]) => Promise<void>
  isLoaded: boolean
}

/**
 * Hook providing reactive tab resolution for the Member app bottom bar.
 * Subscribes to personal tabPrefs and workspace tabDefaults, resolves 4-pinned + More,
 * and tracks unread states with More roll-up (TB-P3, TB-D2).
 */
export function useMemberTabBar(options: UseMemberTabBarOptions = {}): UseMemberTabBarResult {
  const {
    uid,
    workspaceId,
    role = 'ADMIN',
    workspace,
    policy,
    useCase,
    unreadByTab,
    personal: overridePersonal,
  } = options

  const [tabPrefs, setTabPrefs] = useState<TabPrefs | null>(null)
  const [tabDefaults, setTabDefaults] = useState<WorkspaceTabDefaults | null>(null)
  const [subscribedControls, setSubscribedControls] = useState<MemberControls | null>(null)
  const controls = options.effectiveControls !== undefined ? options.effectiveControls : subscribedControls
  const [isLoaded, setIsLoaded] = useState(!uid || !workspaceId)

  useEffect(() => {
    if (options.effectiveControls !== undefined || !workspaceId) {
      return
    }
    return subscribeToMyEffectiveControls(
      workspaceId,
      (loadedControls) => {
        setSubscribedControls(loadedControls)
      },
      uid,
    )
  }, [workspaceId, uid, options.effectiveControls])

  useEffect(() => {
    if (!uid || !workspaceId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- resets loaded status when no id
      setIsLoaded(true)
      return
    }

    let prefsLoaded = false
    let defaultsLoaded = false
    const checkLoaded = () => {
      if (prefsLoaded && defaultsLoaded) setIsLoaded(true)
    }

    const unsubPrefs = subscribeTabPrefs(uid, workspaceId, (loaded) => {
      setTabPrefs(loaded)
      prefsLoaded = true
      checkLoaded()
    })

    const unsubDefaults = subscribeWorkspaceTabDefaults(workspaceId, (defaults) => {
      setTabDefaults(defaults)
      defaultsLoaded = true
      checkLoaded()
    })

    return () => {
      unsubPrefs()
      unsubDefaults()
    }
  }, [uid, workspaceId])

  const effectivePersonal = overridePersonal ?? (tabPrefs?.member as MemberTabKey[] | undefined)

  const hiddenPages = useMemo(() => {
    if (options.personHiddenPages) return options.personHiddenPages
    if (Array.isArray(controls?.tiles)) return controls.tiles as TabKey[]
    return undefined
  }, [options.personHiddenPages, controls?.tiles])

  const resolved = useMemo(() => {
    return resolveTabBar({
      surface: 'member',
      role,
      workspace,
      policy,
      workspaceDefaults: tabDefaults ?? undefined,
      personal: effectivePersonal,
      useCase,
      personHiddenPages: hiddenPages,
    })
  }, [effectivePersonal, workspace, policy, role, tabDefaults, useCase, hiddenPages])

  const pinned = resolved.pinned as MemberTabKey[]
  const more = resolved.more as MemberTabKey[]

  const isTabUnread = useCallback(
    (key: MemberTabKey) => {
      const val = unreadByTab?.[key]
      if (typeof val === 'number') return val > 0
      return Boolean(val)
    },
    [unreadByTab],
  )

  const moreHasUnread = useMemo(() => {
    return more.some((key) => isTabUnread(key))
  }, [isTabUnread, more])

  const savePersonalTabs = useCallback(
    async (tabs: MemberTabKey[]) => {
      setTabPrefs((prev) => ({
        ...prev,
        member: tabs,
      }))
      if (!uid || !workspaceId) return
      try {
        await saveTabPrefs(uid, workspaceId, { member: tabs })
      } catch (err) {
        if (import.meta.env.DEV) {
          console.warn('[useMemberTabBar] Remote tab preferences save failed, keeping local layout:', err)
        }
      }
    },
    [uid, workspaceId],
  )

  return {
    pinned,
    more,
    locked: resolved.locked,
    moreHasUnread,
    isTabUnread,
    savePersonalTabs,
    isLoaded,
  }
}
